import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  Typography,
  Alert,
} from "@mui/material";
import { ImportExport, SwapHoriz, History } from "@mui/icons-material";
import { toast } from "react-toastify";
import TableComponent from "../../components/Table";
import theme from "../../theme/theme";
import {
  fetchStockLevels,
  adjustStock,
  fetchAllWarehouses,
  fetchAllItems,
  fetchWarehouseItems,
  fetchTransactions,
  fetchAllItemCategories,
} from "../../api/InventoryService";

const StockLevelsPage = () => {
  const navigate = useNavigate();
  const [stock, setStock] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [links, setLinks] = useState([]);
  const [meta, setMeta] = useState({ from: 0, to: 0, total: 0 });

  // Filters
  const [filters, setFilters] = useState({
    warehouse_id: "",
    category_id: "",
    item_id: "",
  });

  // History Dialog State
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyItem, setHistoryItem] = useState(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPerPage, setHistoryPerPage] = useState(15);

  // Dialog State for Adjustment
  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogItems, setDialogItems] = useState([]);
  const [loadingDialogItems, setLoadingDialogItems] = useState(false);
  const [formData, setFormData] = useState({
    warehouse_id: "",
    category_id: "",
    item_id: "",
    transaction_type: "in",
    quantity: "",
    notes: "",
  });

  const loadDependencies = async () => {
    try {
      const [wRes, iRes, cRes] = await Promise.all([
        fetchAllWarehouses(),
        fetchAllItems(),
        fetchAllItemCategories(),
      ]);
      setWarehouses(wRes.data || []);
      setItems(iRes.data || []);
      setCategories(cRes.data?.data || cRes.data || []);
    } catch (e) {
      toast.error("Failed to load inventory dependencies");
    }
  };

  const loadStock = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchStockLevels({ page: currentPage, per_page: perPage, ...filters });
      setStock(res.data.data || []);
      setLinks(res.data.links || []);
      setMeta({ from: res.data.from, to: res.data.to, total: res.data.total });
    } catch (error) {
      toast.error("Failed to fetch stock levels");
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, filters]);

  useEffect(() => {
    loadDependencies();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [perPage]);

  useEffect(() => {
    loadStock();
  }, [loadStock]);

  // Load items that exist in the selected warehouse
  const loadWarehouseItems = async (warehouseId) => {
    if (!warehouseId) {
      setDialogItems([]);
      return;
    }
    setLoadingDialogItems(true);
    try {
      const res = await fetchWarehouseItems(warehouseId);
      setDialogItems(res.data || []);
    } catch (e) {
      toast.error("Failed to load warehouse items");
      setDialogItems([]);
    } finally {
      setLoadingDialogItems(false);
    }
  };

  const handleWarehouseChangeInDialog = (warehouseId) => {
    setFormData({
      ...formData,
      warehouse_id: warehouseId,
      category_id: "",
      item_id: "",
    });
    loadWarehouseItems(warehouseId);
  };

  const loadHistory = async (row, page = 1) => {
    setHistoryLoading(true);
    try {
      const res = await fetchTransactions({
        warehouse_id: row.warehouse_id,
        item_id: row.item_id,
        page,
      });
      const paginated = res.data;
      setHistoryData(paginated.data || []);
      setHistoryTotal(paginated.total || 0);
      setHistoryPerPage(paginated.per_page || 15);
    } catch (e) {
      toast.error("Failed to load transaction history");
      setHistoryData([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistory = async (row) => {
    setHistoryItem(row);
    setHistoryOpen(true);
    setHistoryPage(0);
    loadHistory(row, 1);
  };

  const handleHistoryPageChange = (event, newPage) => {
    setHistoryPage(newPage);
    loadHistory(historyItem, newPage + 1);
  };

  const handleOpenDialog = (row = null) => {
    if (row) {
      setFormData({
        warehouse_id: row.warehouse_id,
        category_id: row.item?.category_id || "",
        item_id: row.item_id,
        transaction_type: "in",
        quantity: "",
        notes: "",
      });
      loadWarehouseItems(row.warehouse_id);
    } else {
      setFormData({
        warehouse_id: "",
        category_id: "",
        item_id: "",
        transaction_type: "in",
        quantity: "",
        notes: "",
      });
      setDialogItems([]);
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (
      !formData.warehouse_id ||
      !formData.item_id ||
      !formData.quantity ||
      formData.quantity <= 0
    ) {
      return toast.warning("Warehouse, Item, and valid Quantity are required");
    }
    const selectedItem = dialogItems.find((wi) => wi.item_id === formData.item_id);
    if (formData.transaction_type === "out") {
      if (!selectedItem || selectedItem.quantity <= 0) {
        return toast.error("Stock is zero. Cannot perform OUT transaction.");
      }
      if (formData.quantity > selectedItem.quantity) {
        return toast.error(`Insufficient stock. Available: ${selectedItem.quantity}`);
      }
    }
    setSaving(true);
    try {
      await adjustStock(formData);
      toast.success("Stock adjusted successfully");
      setOpenDialog(false);
      loadStock();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to adjust stock");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      field: "warehouse.name",
      headerName: "Warehouse",
    },
    {
      field: "item.name",
      headerName: "Item Name",
    },
    {
      field: "item.sku",
      headerName: "SKU",
    },
    {
      field: "item.category.name",
      headerName: "Category",
    },
    {
      field: "quantity",
      headerName: "Current Stock",

    },
  ];

  const actions = [
    {
      icon: <History />,
      label: "History",
      onClick: (row) => handleOpenHistory(row),
      backgroundColor: "#455a64",
    },
    {
      icon: <ImportExport />,
      label: "Adjust Stock",
      onClick: (row) => handleOpenDialog(row),
      backgroundColor: theme.button.editBackgroundColor,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          Stock Levels
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SwapHoriz />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: theme.button.borderRadius }}
        >
          Manual Adjustment
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <FormControl
          size="small"
          sx={{ minWidth: 200, backgroundColor: "#fff" }}
        >
          <InputLabel>Warehouse</InputLabel>
          <Select
            label="Warehouse"
            value={filters.warehouse_id}
            onChange={(e) => {
              setFilters({ ...filters, warehouse_id: e.target.value });
              setCurrentPage(1);
            }}
          >
            <MenuItem value="">
              <em>All Warehouses</em>
            </MenuItem>
            {warehouses.map((w) => (
              <MenuItem key={w.id} value={w.id}>
                {w.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{ minWidth: 200, backgroundColor: "#fff" }}
        >
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={filters.category_id}
            onChange={(e) => {
              setFilters({
                ...filters,
                category_id: e.target.value,
                item_id: "",
              });
              setCurrentPage(1);
            }}
          >
            <MenuItem value="">
              <em>All Categories</em>
            </MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{ minWidth: 300, backgroundColor: "#fff" }}
        >
          <InputLabel>Item</InputLabel>
          <Select
            label="Item"
            value={filters.item_id}
            onChange={(e) => {
              setFilters({ ...filters, item_id: e.target.value });
              setCurrentPage(1);
            }}
          >
            <MenuItem value="">
              <em>All Items</em>
            </MenuItem>
            {items
              .filter(
                (i) =>
                  !filters.category_id ||
                  i.category_id === filters.category_id,
              )
              .map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  {i.sku} - {i.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          onClick={() => {
            setFilters({ warehouse_id: "", category_id: "", item_id: "" });
            setCurrentPage(1);
          }}
        >
          Clear Filters
        </Button>
      </Box>

      <TableComponent
        columns={columns}
        data={stock}
        actions={actions}
        loading={loading}
        links={links}
        setCurrentPage={setCurrentPage}
        meta={meta}
        perPage={perPage}
        setPerPage={setPerPage}
      />

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adjust Stock</DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}
        >
          <FormControl fullWidth required>
            <InputLabel>Warehouse</InputLabel>
            <Select
              label="Warehouse"
              value={formData.warehouse_id}
              onChange={(e) => handleWarehouseChangeInDialog(e.target.value)}
            >
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            required
            disabled={!formData.warehouse_id || loadingDialogItems}
          >
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={formData.category_id}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  category_id: e.target.value,
                  item_id: "",
                })
              }
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            required
            disabled={
              !formData.warehouse_id ||
              !formData.category_id ||
              loadingDialogItems
            }
          >
            <InputLabel>{loadingDialogItems ? "Loading items..." : "Item (existing in warehouse)"}</InputLabel>
            <Select
              label={loadingDialogItems ? "Loading items..." : "Item (existing in warehouse)"}
              value={formData.item_id}
              onChange={(e) =>
                setFormData({ ...formData, item_id: e.target.value })
              }
            >
              {(() => {
                const filtered = dialogItems.filter(
                  (wi) =>
                    !formData.category_id ||
                    wi.item?.category_id === formData.category_id,
                );
                if (filtered.length === 0 && !loadingDialogItems) {
                  return (
                    <MenuItem value="" disabled>
                      <em>
                        {!formData.warehouse_id
                          ? "Select a warehouse first"
                          : !formData.category_id
                            ? "Select a category first"
                            : "No items in this category for this warehouse"}
                      </em>
                    </MenuItem>
                  );
                }
                return filtered.map((wi) => (
                  <MenuItem key={wi.item_id} value={wi.item_id}>
                    {wi.item?.sku} - {wi.item?.name} (Stock: {wi.quantity})
                  </MenuItem>
                ));
              })()}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={formData.transaction_type}
                onChange={(e) =>
                  setFormData({ ...formData, transaction_type: e.target.value })
                }
              >
                <MenuItem value="in">Receiving (IN)</MenuItem>
                <MenuItem
                  value="out"
                  disabled={
                    formData.item_id &&
                    (dialogItems.find((wi) => wi.item_id === formData.item_id)?.quantity || 0) <= 0
                  }
                >
                  Deduction (OUT)
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              required
              fullWidth
              type="number"
              label="Quantity"
              inputProps={{
                min: 1,
                ...(formData.transaction_type === "out" && formData.item_id
                  ? { max: dialogItems.find((wi) => wi.item_id === formData.item_id)?.quantity || 0 }
                  : {}),
              }}
              value={formData.quantity}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  quantity: parseInt(e.target.value) || "",
                })
              }
              helperText={
                formData.transaction_type === "out" && formData.item_id
                  ? `Available: ${dialogItems.find((wi) => wi.item_id === formData.item_id)?.quantity || 0}`
                  : ""
              }
            />
          </Box>
          {formData.item_id &&
            (dialogItems.find((wi) => wi.item_id === formData.item_id)?.quantity || 0) <= 0 && (
              <Alert severity="warning" sx={{ mt: -1 }}>
                This item has zero stock. Only IN (receiving) transactions are allowed.
              </Alert>
            )}

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Notes / Reason"
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            placeholder="e.g. Received from supplier, defective deduction, etc."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Processing..." : "Confirm Adjustment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Transaction History
          {historyItem && (
            <Typography variant="body2" color="text.secondary">
              {historyItem.item?.name} ({historyItem.item?.sku}) — {historyItem.warehouse?.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : historyData.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No transactions found for this item.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell><strong>Sr. No</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell align="right"><strong>Qty</strong></TableCell>
                    <TableCell><strong>Reference</strong></TableCell>
                    <TableCell><strong>Notes</strong></TableCell>
                    <TableCell><strong>By</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.map((tx, idx) => (
                    <TableRow key={tx.id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {new Date(tx.created_at).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}{" "}
                        {new Date(tx.created_at).toLocaleTimeString("en-GB", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tx.transaction_type === "in" ? "IN" : "OUT"}
                          size="small"
                          color={tx.transaction_type === "in" ? "success" : "error"}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {tx.transaction_type === "in" ? "+" : "-"}{tx.quantity}
                      </TableCell>
                      <TableCell>
                        {tx.project_item_transaction?.project_id ? (
                          <Button
                            size="small"
                            variant="text"
                            sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                            onClick={() => {
                              setHistoryOpen(false);
                              navigate(
                                `/projects/${tx.project_item_transaction.project_id}`
                              );
                            }}
                          >
                            {tx.reference || "View Project"}
                          </Button>
                        ) : (
                          tx.reference || "—"
                        )}
                      </TableCell>
                      <TableCell>{tx.notes || "—"}</TableCell>
                      <TableCell>{tx.performer?.name || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {historyTotal > historyPerPage && (
            <TablePagination
              component="div"
              count={historyTotal}
              page={historyPage}
              onPageChange={handleHistoryPageChange}
              rowsPerPage={historyPerPage}
              rowsPerPageOptions={[historyPerPage]}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockLevelsPage;
